import { ApiError } from '../api/http';
import { hasOwnCode, plainFailure } from '../api/plain-failure';

/**
 * What a person is told when a sign-in goes wrong.
 *
 * The sign-in service has its own words for its own failures, and they name the vendor
 * ("Firebase: Error (auth/network-request-failed)."). The browser has its own too ("Failed to
 * fetch"), and so does a server that fell over ("Internal Server Error"). Paige never shows any of
 * them: no model, library or vendor name appears anywhere a customer can see, error messages
 * included (`docs/ui-concept.md`, Branding). So a known code becomes Paige's line, a refusal the API
 * wrote a sentence for is shown in that sentence, a request nothing answered becomes "could not be
 * reached", and everything else is the caller's fallback.
 */

/** The sign-in service could not be reached. */
export const unreachable =
  'Paige could not reach the sign-in service. Check your connection and try again.';

/** Nothing answered at all: offline, or Paige is down. */
export const notReached = 'Paige could not be reached. Check your connection and try again.';

export function plainMessage(e: unknown, words: Record<string, string>, fallback: string): string {
  const code = typeof e === 'object' && e !== null && 'code' in e ? String(e.code) : '';
  const known = words[code];
  if (known) return known;
  if (e instanceof ApiError) return paigeSentence(e) ?? fallback;
  // fetch rejects with a TypeError when no answer came back; its message is the browser's.
  if (e instanceof TypeError) return notReached;
  return fallback;
}

/**
 * The API's sentence for a refusal, when it wrote one for a person. A 400 or 422 with no code of its
 * own names a field of a body the page built ("acquisition.visitor is not a known short string
 * field", "company is required"), and a server error's detail is nobody's sentence: null for both.
 */
export function paigeSentence(e: ApiError): string | null {
  const malformed = (e.status === 400 || e.status === 422) && !hasOwnCode(e);
  return malformed ? null : plainFailure(e, '') || null;
}
